import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../Configurations/Urls";

export async function fetchEmsBilling() {
  const res = await axios.get(`${MAIN_URL}/api/ems/billing`, {
    headers: getApiHeaders(),
  });
  return res.data;
}

export async function createEmsBillingOrder(planId) {
  const res = await axios.post(
    `${MAIN_URL}/api/ems/billing/create-order`,
    { plan_id: Number(planId) },
    { headers: getApiHeaders() }
  );
  return res.data;
}

export async function verifyEmsBillingPayment(payload) {
  const res = await axios.post(
    `${MAIN_URL}/api/ems/billing/verify-payment`,
    payload,
    { headers: getApiHeaders() }
  );
  return res.data;
}

/** Legacy direct upgrade (no Razorpay). Prefer verifyEmsBillingPayment. */
export async function upgradeEmsPlan(planId, paymentReference = null) {
  const res = await axios.post(
    `${MAIN_URL}/api/ems/billing/upgrade`,
    {
      plan_id: Number(planId),
      payment_reference: paymentReference,
    },
    { headers: getApiHeaders() }
  );
  return res.data;
}

async function fetchDocBlob(clientPaymentId, { type = "invoice", view = false } = {}) {
  const params = new URLSearchParams();
  params.set("type", type);
  if (view) params.set("view", "1");

  const res = await axios.get(
    `${MAIN_URL}/api/ems/billing/receipts/${Number(clientPaymentId)}?${params}`,
    {
      headers: getApiHeaders(),
      responseType: "blob",
    }
  );

  const contentType = String(res.headers["content-type"] || "");
  if (contentType.includes("application/json")) {
    const text = await res.data.text();
    const json = JSON.parse(text);
    throw new Error(json.error || json.message || "Unable to load document.");
  }

  return res.data;
}

function parseBlobError(err) {
  if (err?.response?.data instanceof Blob) {
    return err.response.data.text().then((text) => {
      try {
        const json = JSON.parse(text);
        const error = new Error(
          json.error || json.message || "Unable to load document."
        );
        error.response = { data: json };
        throw error;
      } catch (inner) {
        if (inner.response) throw inner;
        throw err;
      }
    });
  }
  return Promise.reject(err);
}

export async function downloadEmsReceipt(
  clientPaymentId,
  fileName,
  type = "invoice"
) {
  try {
    const data = await fetchDocBlob(clientPaymentId, { type, view: false });
    const blob = new Blob([data], { type: "text/html;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName || `${type === "receipt" ? "RCP" : "INV"}-EMS-${clientPaymentId}`}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    return parseBlobError(err);
  }
}

/** Open invoice or receipt HTML in a new tab. */
export async function viewEmsInvoice(clientPaymentId, type = "invoice") {
  try {
    const data = await fetchDocBlob(clientPaymentId, { type, view: true });
    const blob = new Blob([data], { type: "text/html;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    return parseBlobError(err);
  }
}

export function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (typeof window !== "undefined" && window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}
